import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import {
  FolderOpen,
  Camera,
  Eye,
  ImageOff,
  Plus,
  ChevronRight,
  Loader2,
  Share2,
  UserPlus,
  X,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn, formatRelativeTime } from '@/lib/utils'
import { cameraGroupsApi, camerasApi } from '@/api/endpoints'
import { toast } from '@/lib/toast'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { useDirectionStore } from '@/store/directionStore'
import type { CameraGroup } from '@/types'

// Single thumbnail tile with skeleton placeholder until loaded.
// `loading="lazy"` defers off-screen image fetches until the card scrolls in
// (#036 — meaningful improvement when a user has many groups).
function ThumbnailTile({ src }: { src: string | null }) {
  const [loaded, setLoaded] = useState(false)
  if (!src) {
    return (
      <div className="flex-1 relative bg-muted overflow-hidden h-full flex items-center justify-center">
        <ImageOff className="h-4 w-4 text-muted-foreground/25" />
      </div>
    )
  }
  return (
    <div className="flex-1 relative bg-muted overflow-hidden">
      {/* Skeleton: shimmer placeholder visible until the image paints. */}
      {!loaded && <div className="absolute inset-0 bg-muted animate-pulse" />}
      <img
        src={src}
        alt=""
        loading="lazy"
        className={cn(
          'h-full w-full object-cover transition-opacity duration-200',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
          setLoaded(true)  // hide the shimmer; img is gone, the bg shows through
        }}
      />
    </div>
  )
}

// Thumbnail strip — shows up to 3 latest detection images for cameras in a group
function ThumbnailStrip({ thumbnails }: { thumbnails: (string | null)[] }) {
  const shown = thumbnails.slice(0, 3)
  return (
    <div className="flex gap-0.5 h-28 overflow-hidden">
      {shown.map((src, i) => (
        <ThumbnailTile key={i} src={src} />
      ))}
      {shown.length === 0 && (
        <div className="flex-1 flex items-center justify-center bg-muted">
          <Camera className="h-6 w-6 text-muted-foreground/25" />
        </div>
      )}
    </div>
  )
}

function GroupCard({ group, onClick, onShare }: { group: CameraGroup; onClick: () => void; onShare?: () => void }) {
  const { t } = useTranslation('groups')
  const d = useDirectionStore((s) => s.direction)
  const cameras = group.cameras_details
  const thumbnails = cameras
    .filter((c) => c.latest_detection_thumbnail)
    .slice(0, 3)
    .map((c) => c.latest_detection_thumbnail)
  const totalDetections = cameras.reduce((sum, c) => sum + c.total_detections, 0)
  // Latest detection across any camera in the group (#037 cont.)
  const latestDetection = cameras
    .map((c) => c.last_detection_timestamp)
    .filter((t): t is string => Boolean(t))
    .sort()
    .at(-1)

  return (
    <Card
      className="overflow-hidden cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all duration-150 group"
      onClick={onClick}
    >
      <ThumbnailStrip thumbnails={thumbnails} />
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
            <CardTitle className={cn('text-body font-semibold truncate', d === 'expressive' && 'font-display')}>{group.name}</CardTitle>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {/* Share — owner only (recipients can't re-share; backend 403s) (#096) */}
            {group.is_owner !== false && onShare && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onShare() }}
                title={t('share.button')}
                aria-label={t('share.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors"
              >
                <Share2 className="h-3.5 w-3.5" />
              </button>
            )}
            <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
          </div>
        </div>
        {/* Recipient badge (#096) */}
        {group.is_owner === false && (
          <div className="mt-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-micro font-medium text-primary">
              <Share2 className="h-3 w-3" />
              {t('sharedBadge')}
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent className={cn('px-4 pb-4 space-y-3', (d === 'command' || d === 'saas') && 'px-3 pb-3')} >
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-0.5">
            <span className={cn('text-micro text-muted-foreground', d === 'saas' && 'text-[13px]')}>{t('stats.cameras')}</span>
            <p className={cn('text-xs font-semibold', d === 'command' && 'font-mono tabular-nums')}>{group.camera_count}</p>
          </div>
          <div className="space-y-0.5">
            <span className={cn('text-micro text-muted-foreground', d === 'saas' && 'text-[13px]')}>{t('stats.active')}</span>
            <p className={cn('text-xs font-semibold', d === 'command' && 'font-mono tabular-nums')}>{cameras.length}</p>
          </div>
          <div className="space-y-0.5">
            <span className={cn('text-micro text-muted-foreground', d === 'saas' && 'text-[13px]')}>{t('stats.detections')}</span>
            <p className={cn('text-xs font-semibold flex items-center gap-1', d === 'command' && 'font-mono tabular-nums')}>
              <Eye className="h-3 w-3 text-muted-foreground" />
              {totalDetections}
            </p>
          </div>
        </div>

        {/* Camera name list preview */}
        {cameras.length > 0 && (
          <div className={cn('text-xs text-muted-foreground truncate', d === 'saas' && 'text-[13px]')}>
            {cameras
              .slice(0, 3)
              .map((c) => c.name)
              .join(', ')}
            {cameras.length > 3 && ` ${t('more', { count: cameras.length - 3 })}`}
          </div>
        )}

        {/* Latest detection timestamp (relative) */}
        {latestDetection && (
          <div className={cn('flex items-center gap-1 text-xs text-muted-foreground', d === 'saas' && 'text-[13px]')}>
            <Eye className="h-3 w-3 shrink-0" />
            <span className="truncate">Last detection {formatRelativeTime(latestDetection)}</span>
          </div>
        )}

        {/* View cameras link */}
        <div className="flex items-center gap-1 text-xs font-medium text-primary group-hover:text-primary/80 transition-colors pt-1">
          <Camera className="h-3 w-3" />
          {t('viewCameras')}
          <ChevronRight className="h-3 w-3 ml-auto" />
        </div>
      </CardContent>
    </Card>
  )
}

function UngroupedCard({ count, onClick }: { count: number; onClick: () => void }) {
  const { t } = useTranslation('groups')
  return (
    <Card
      className="overflow-hidden cursor-pointer hover:ring-1 hover:ring-muted-foreground/30 transition-all duration-150 group border-dashed"
      onClick={onClick}
    >
      <div className="h-20 bg-muted/30 flex items-center justify-center">
        <Camera className="h-8 w-8 text-muted-foreground/20" />
      </div>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="h-4 w-4 text-muted-foreground/50 shrink-0" />
            <CardTitle className="text-base text-muted-foreground">{t('ungrouped')}</CardTitle>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60 shrink-0 transition-colors" />
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <p className="text-xs text-muted-foreground">
          {t('ungroupedCount', { count })}
        </p>
      </CardContent>
    </Card>
  )
}

function GroupCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="h-20 shimmer" />
      <CardHeader className="pb-2">
        <div className="shimmer h-5 w-32 rounded" />
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div className="shimmer h-8 rounded" />
          <div className="shimmer h-8 rounded" />
          <div className="shimmer h-8 rounded" />
        </div>
        <div className="shimmer h-4 w-40 rounded" />
      </CardContent>
    </Card>
  )
}

// Share-group dialog (#096) — owner shares a group by email; recipients get
// read-only access + push. Reads `group.shared_with` from the (live) group
// passed in; mutations invalidate the groups query so the list refreshes.
function ShareGroupDialog({ group, onClose }: { group: CameraGroup | null; onClose: () => void }) {
  const { t } = useTranslation('groups')
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })

  const shareMutation = useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) => cameraGroupsApi.shareGroup(id, email),
    onSuccess: () => {
      toast.success(t('share.shared'))
      setEmail('')
      invalidate()
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(t('share.shareFailed'), msg)
    },
  })

  const unshareMutation = useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) => cameraGroupsApi.unshareGroup(id, userId),
    onSuccess: () => {
      toast.success(t('share.removed'))
      invalidate()
    },
    onError: () => toast.error(t('share.removeFailed')),
  })

  const sharedWith = group?.shared_with ?? []
  const trimmed = email.trim()

  return (
    <Dialog.Root open={group !== null} onOpenChange={(o) => { if (!o) { setEmail(''); onClose() } }}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ animationDuration: '200ms' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ animationDuration: '200ms' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 shrink-0">
                <Share2 className="h-4 w-4 text-primary" />
              </div>
              <Dialog.Title className="text-base font-semibold truncate">
                {t('share.title', { name: group?.name ?? '' })}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t('share.description')}</p>

          {/* Share by email */}
          <form
            className="flex gap-2 mt-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (group && trimmed) shareMutation.mutate({ id: group.id, email: trimmed })
            }}
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('share.emailPlaceholder')}
              className="h-9 text-sm"
              autoFocus
            />
            <Button type="submit" size="sm" className="h-9 gap-1.5 shrink-0" disabled={shareMutation.isPending || !trimmed}>
              {shareMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              {t('share.shareAction')}
            </Button>
          </form>

          {/* Current members */}
          <div className="mt-5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <Users className="h-3.5 w-3.5" />
              {t('share.sharedWithLabel')}
            </div>
            {sharedWith.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('share.noMembers')}</p>
            ) : (
              <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                {sharedWith.map((u) => (
                  <li key={u.id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.username}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive shrink-0"
                      onClick={() => group && unshareMutation.mutate({ id: group.id, userId: u.id })}
                      disabled={unshareMutation.isPending}
                    >
                      {t('share.remove')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end pt-5">
            <Dialog.Close asChild>
              <Button variant="outline" size="sm">{t('share.close')}</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function GroupsPage() {
  const { t } = useTranslation('groups')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const d = useDirectionStore((s) => s.direction)
  const [newGroupName, setNewGroupName] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [shareGroupId, setShareGroupId] = useState<number | null>(null)

  const createGroupMutation = useMutation({
    mutationFn: (name: string) => cameraGroupsApi.create({ name }),
    onSuccess: () => {
      toast.success(t('groupCreated'))
      setNewGroupName('')
      setShowCreateForm(false)
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
    },
    onError: () => toast.error(t('createFailed')),
  })

  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: queryKeys.cameraGroups.all(),
    queryFn: () => cameraGroupsApi.getAll(),
    ...queryOptions.cameraGroups.all,
  })

  const { data: cameras = [] } = useQuery({
    queryKey: ['cameras', 'all', ''],
    queryFn: () => camerasApi.getAll(),
    ...queryOptions.cameras.all,
  })

  // Page is "ready" as soon as groups have loaded. The cameras query is
  // only needed for the ungrouped-card count (#036) — gating the whole
  // skeleton on it adds 2-4s of perceived load with no visible benefit
  // because /api/cameras/ pulls the full fleet (~250 cameras for some users).
  // The ungrouped card simply pops in when its data arrives.
  const isLoading = groupsLoading
  const ungroupedCount = cameras.filter((c) => c.group_id === null).length
  // Derive from live query data (not a snapshot) so the dialog's shared_with
  // list refreshes after share/unshare invalidates the query (#096).
  const shareGroup = groups.find((g) => g.id === shareGroupId) ?? null

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-up">
        <div>
          <h2 className={cn('text-xl sm:text-2xl font-semibold tracking-tight', d === 'expressive' && 'font-display')}>{t('pageTitle')}</h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('subtitle')}
            {!isLoading && groups.length > 0 && ` ${t('groupCount', { count: groups.length })}`}
          </p>
        </div>
        <Button
          className="w-full sm:w-auto"
          onClick={() => setShowCreateForm(f => !f)}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('createGroup')}
        </Button>
      </div>

      {/* Inline create group form */}
      {showCreateForm && (
        <div className="flex gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <Input
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder={t('groupNamePlaceholder')}
            className="h-9 text-sm max-w-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newGroupName.trim()) createGroupMutation.mutate(newGroupName.trim())
              if (e.key === 'Escape') { setNewGroupName(''); setShowCreateForm(false) }
            }}
            autoFocus
          />
          <Button
            size="sm"
            className="h-9 text-xs gap-1.5"
            onClick={() => {
              if (newGroupName.trim()) createGroupMutation.mutate(newGroupName.trim())
            }}
            disabled={createGroupMutation.isPending || !newGroupName.trim()}
          >
            {createGroupMutation.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Plus className="h-3.5 w-3.5" />
            }
            {tc('actions.create')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-xs"
            onClick={() => { setNewGroupName(''); setShowCreateForm(false) }}
          >
            {tc('actions.cancel')}
          </Button>
        </div>
      )}

      {/* Skeletons */}
      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => <GroupCardSkeleton key={i} />)}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && groups.length === 0 && (
        <Card>
          <CardContent className="flex h-64 flex-col items-center justify-center text-center gap-4">
            <FolderOpen className="h-12 w-12 text-muted-foreground/30" />
            <div>
              <h3 className="text-lg font-semibold">{t('empty.title')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('empty.message')}
              </p>
            </div>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('createGroup')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Group cards */}
      {!isLoading && groups.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onClick={() => navigate(`/cameras?group=${group.id}`)}
              onShare={() => setShareGroupId(group.id)}
            />
          ))}

          {/* Ungrouped card — only show if there are ungrouped cameras */}
          {ungroupedCount > 0 && (
            <UngroupedCard
              count={ungroupedCount}
              onClick={() => navigate('/cameras?group=ungrouped')}
            />
          )}
        </div>
      )}

      {/* Share-group dialog (#096) */}
      <ShareGroupDialog group={shareGroup} onClose={() => setShareGroupId(null)} />
    </div>
  )
}
